import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { toast } from 'sonner';
import { Upload, Link as LinkIcon, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ProfileEditModal({ open, onOpenChange, profile, onUpdate }) {
  const [formData, setFormData] = useState({
    display_name: profile?.display_name || '',
    bio: profile?.bio || '',
    location: profile?.location || '',
    profile_picture: profile?.profile_picture || ''
  });
  const [avatarMode, setAvatarMode] = useState('url'); // 'url' or 'upload'
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5MB)');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/api/upload/avatar`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, profile_picture: data.url }));
        toast.success('Avatar uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/social/user/profile`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Profile updated');
        onUpdate?.();
        onOpenChange(false);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Avatar Section */}
          <div className="flex flex-col items-center gap-4">
            <Avatar className="w-24 h-24">
              <AvatarImage src={formData.profile_picture} />
              <AvatarFallback>{formData.display_name?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>

            <div className="flex gap-2">
              <Button
                variant={avatarMode === 'url' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAvatarMode('url')}
              >
                <LinkIcon className="w-4 h-4 mr-1" />
                URL
              </Button>
              <Button
                variant={avatarMode === 'upload' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAvatarMode('upload')}
              >
                <Upload className="w-4 h-4 mr-1" />
                Upload
              </Button>
            </div>

            {avatarMode === 'url' ? (
              <Input
                placeholder="https://example.com/avatar.jpg"
                value={formData.profile_picture}
                onChange={(e) => setFormData({ ...formData, profile_picture: e.target.value })}
              />
            ) : (
              <div className="w-full">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  disabled={uploading}
                  className="w-full"
                />
                {uploading && <p className="text-sm text-gray-500 mt-1">Uploading...</p>}
              </div>
            )}
          </div>

          {/* Display Name */}
          <div>
            <Label>Display Name</Label>
            <Input
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              maxLength={50}
              placeholder="Your display name"
            />
          </div>

          {/* Bio */}
          <div>
            <Label>Bio</Label>
            <Textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              maxLength={500}
              rows={3}
              placeholder="Tell us about yourself..."
            />
            <p className="text-xs text-gray-500 mt-1">{formData.bio.length}/500</p>
          </div>

          {/* Location */}
          <div>
            <Label>Location</Label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              maxLength={100}
              placeholder="City, Country"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
